import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { useYetki } from '@/lib/yetki';
import { InviteLinkBox } from '@/components/ui/InviteLinkBox';
import { Button, Card, Input, Label, Spinner, Textarea } from '@/components/ui';

export function OrganizationEditPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    donationLink: '',
    youtubeLink: '',
    readingSourceLink: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => api<{ organization: Record<string, unknown> }>(`/organizations/${orgId}`),
    enabled: !!orgId,
  });

  /* Organizasyon ayarları yalnız yöneticinin. Bağış ve YouTube bağları
     buradan konuyor; katılımcı adresi bilerek açıp değiştirebiliyordu. */
  const { yonetici, yukleniyor: yetkiYukleniyor } = useYetki(orgId);

  useEffect(() => {
    const org = data?.organization;
    if (!org) return;
    setForm({
      name: (org.name as string) ?? '',
      description: (org.description as string) ?? '',
      donationLink: (org.donationLink as string) ?? '',
      youtubeLink: (org.youtubeLink as string) ?? '',
      readingSourceLink: (org.readingSourceLink as string) ?? '',
    });
  }, [data]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      await api(`/organizations/${orgId}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    } finally {
      setLoading(false);
    }
  }

  if (isLoading || yetkiYukleniyor) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!yonetici) {
    return (
      <div className="space-y-4">
        <PageHeader title="Organizasyon" backTo="/dashboard" />
        <Card>
          <p className="text-sm text-muted-foreground">
            Organizasyon ayarları yöneticilere açık.
          </p>
        </Card>
      </div>
    );
  }

  const inviteCode = (data?.organization?.inviteCode as string) ?? '';

  return (
    <div className="space-y-4">
      <PageHeader title="Organizasyon Düzenle" subtitle="Grup bilgilerini güncelleyin" backTo="/dashboard" />

      {inviteCode && (
        <InviteLinkBox
          inviteCode={inviteCode}
          label="Organizasyon Davet Linki"
          onRegenerate={async () => {
            const res = await api<{ inviteCode: string }>(`/organizations/${orgId}/regenerate-invite`, {
              method: 'POST',
            });
            return res.inviteCode;
          }}
        />
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Organizasyon Adı</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label>Bağış Linki</Label>
            <Input
              type="url"
              placeholder="https://..."
              value={form.donationLink}
              onChange={(e) => setForm({ ...form, donationLink: e.target.value })}
            />
          </div>
          <div>
            <Label>YouTube Linki</Label>
            <Input
              type="url"
              placeholder="https://youtube.com/..."
              value={form.youtubeLink}
              onChange={(e) => setForm({ ...form, youtubeLink: e.target.value })}
            />
          </div>
          <div>
            <Label>Okuma Kaynağı Linki</Label>
            <Input
              type="url"
              placeholder="https://..."
              value={form.readingSourceLink}
              onChange={(e) => setForm({ ...form, readingSourceLink: e.target.value })}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Spinner /> : 'Kaydet'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
